import { useCallback, useMemo } from "react";
import { useViewerActions, useViewerStore } from "../../stores/viewerStore";
import { useRotationGizmoVisibility } from "../../selected-body-transform-gizmo/rotationGizmoVisibility";
import { useScaleGizmoVisibility } from "../../selected-body-transform-gizmo/scaleGizmoVisibility";
import { useShortcutAction } from "../../shortcuts/useShortcutAction";
import { SHORTCUT_ACTIONS } from "../../shortcuts/defaultBindings";

export function useTransformGizmoControls({
  disabled = false,
  enableShortcuts = true,
  enableScale = false,
} = {}) {
  const viewerActions = useViewerActions();
  const showPositionGizmo = useViewerStore((s) => s.showPositionGizmo);
  const [showRotationGizmo, setShowRotationGizmo] = useRotationGizmoVisibility();
  const [showScaleGizmo, setShowScaleGizmo] = useScaleGizmoVisibility();

  const togglePositionGizmo = useCallback(() => {
    if (!showPositionGizmo && showRotationGizmo) {
      setShowRotationGizmo(false);
    }
    if (!showPositionGizmo && showScaleGizmo) {
      setShowScaleGizmo(false);
    }
    viewerActions.toggleShowPositionGizmo();
  }, [
    showPositionGizmo,
    showRotationGizmo,
    showScaleGizmo,
    setShowRotationGizmo,
    setShowScaleGizmo,
    viewerActions,
  ]);

  const toggleRotationGizmo = useCallback(() => {
    const next = !showRotationGizmo;
    if (next && showPositionGizmo) {
      viewerActions.toggleShowPositionGizmo();
    }
    if (next && showScaleGizmo) {
      setShowScaleGizmo(false);
    }
    setShowRotationGizmo(next);
  }, [
    showPositionGizmo,
    showRotationGizmo,
    showScaleGizmo,
    setShowRotationGizmo,
    setShowScaleGizmo,
    viewerActions,
  ]);

  const toggleScaleGizmo = useCallback(() => {
    if (!enableScale) return;
    const next = !showScaleGizmo;
    if (next && showPositionGizmo) {
      viewerActions.toggleShowPositionGizmo();
    }
    if (next && showRotationGizmo) {
      setShowRotationGizmo(false);
    }
    setShowScaleGizmo(next);
  }, [
    enableScale,
    showPositionGizmo,
    showRotationGizmo,
    showScaleGizmo,
    setShowRotationGizmo,
    setShowScaleGizmo,
    viewerActions,
  ]);

  const exitGizmoMode = useCallback(() => {
    if (!showPositionGizmo && !showRotationGizmo && !showScaleGizmo) {
      return false;
    }
    if (showPositionGizmo) {
      viewerActions.toggleShowPositionGizmo();
    }
    if (showRotationGizmo) {
      setShowRotationGizmo(false);
    }
    if (showScaleGizmo) {
      setShowScaleGizmo(false);
    }
    return true;
  }, [
    showPositionGizmo,
    showRotationGizmo,
    showScaleGizmo,
    setShowRotationGizmo,
    setShowScaleGizmo,
    viewerActions,
  ]);

  const cycleGizmoMode = useCallback(() => {
    if (showPositionGizmo) {
      viewerActions.toggleShowPositionGizmo();
      setShowRotationGizmo(true);
      return;
    }
    if (showRotationGizmo) {
      setShowRotationGizmo(false);
      if (enableScale) {
        setShowScaleGizmo(true);
      } else {
        viewerActions.toggleShowPositionGizmo();
      }
      return;
    }
    if (showScaleGizmo) {
      setShowScaleGizmo(false);
      viewerActions.toggleShowPositionGizmo();
      return;
    }
    // If neither is active, start in translate mode.
    viewerActions.toggleShowPositionGizmo();
  }, [
    enableScale,
    showPositionGizmo,
    showRotationGizmo,
    showScaleGizmo,
    setShowRotationGizmo,
    setShowScaleGizmo,
    viewerActions,
  ]);

  const shortcutOptions = useMemo(
    () => ({
      allowInEditable: false,
      preventDefault: true,
      priority: 20,
    }),
    []
  );

  useShortcutAction(
    enableShortcuts ? SHORTCUT_ACTIONS.GIZMO_POSITION_TOGGLE : null,
    () => {
      if (disabled) return false;
      togglePositionGizmo();
      return true;
    },
    shortcutOptions,
    [disabled, togglePositionGizmo]
  );

  useShortcutAction(
    enableShortcuts ? SHORTCUT_ACTIONS.GIZMO_ROTATION_TOGGLE : null,
    () => {
      if (disabled) return false;
      toggleRotationGizmo();
      return true;
    },
    shortcutOptions,
    [disabled, toggleRotationGizmo]
  );

  useShortcutAction(
    enableShortcuts ? SHORTCUT_ACTIONS.GIZMO_CYCLE : null,
    () => {
      if (disabled) return false;
      cycleGizmoMode();
      return true;
    },
    shortcutOptions,
    [disabled, cycleGizmoMode]
  );

  useShortcutAction(
    enableShortcuts ? SHORTCUT_ACTIONS.GIZMO_EXIT : null,
    () => {
      if (disabled) return false;
      return exitGizmoMode();
    },
    shortcutOptions,
    [disabled, exitGizmoMode]
  );

  return {
    showPositionGizmo,
    showRotationGizmo,
    showScaleGizmo: enableScale ? showScaleGizmo : false,
    togglePositionGizmo,
    toggleRotationGizmo,
    toggleScaleGizmo: enableScale ? toggleScaleGizmo : undefined,
  };
}
